import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faBars, faCog, faDatabase, faSave, faTable, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import axios from "axios";
import { BAlert, BButton, BSpinner, BTab, BTabs } from "bootstrap-vue";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { GalaxyApi } from "@/api";
import { updateContentFields } from "@/components/History/model/queries";
import { DatatypesProvider, DbKeyProvider, SuitableConvertersProvider } from "@/components/providers";
import { useConfig } from "@/composables/config";
import { useCollectionAttributesStore } from "@/stores/collectionAttributesStore";
import { useCollectionElementsStore } from "@/stores/collectionElementsStore";
import { useHistoryStore } from "@/stores/historyStore";
import localize from "@/utils/localization";
import { prependPath } from "@/utils/redirect";
import { errorMessageAsString } from "@/utils/simple-error";
import ChangeDatatypeTab from "@/components/Collections/common/ChangeDatatypeTab.vue";
import DatabaseEditTab from "@/components/Collections/common/DatabaseEditTab.vue";
import SuitableConvertersTab from "@/components/Collections/common/SuitableConvertersTab.vue";
import Heading from "@/components/Common/Heading.vue";
import FormDisplay from "@/components/Form/FormDisplay.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'CollectionEditView',
    props: {
        collectionId: null
    },
    setup(__props) {
        const props = __props;
        library.add(faBars, faCog, faDatabase, faSave, faTable, faUser);
        const { config, isConfigLoaded } = useConfig(true);
        const collectionAttributesStore = useCollectionAttributesStore();
        const historyStore = useHistoryStore();
        const { currentHistoryId } = storeToRefs(historyStore);
        const collectionStore = useCollectionElementsStore();
        const jobError = ref(null);
        const errorMessage = ref("");
        const infoMessage = ref("");
        const successMessage = ref("");
        const attributesInputs = ref([]);
        /** Used to track if there has been a change in the collection, and rerenders `FormDisplay` */
        const collectionChangeKey = ref(0);
        const attributesData = computed(() => {
            return collectionAttributesStore.getAttributes(props.collectionId);
        });
        const attributesLoadError = computed(() => {
            const itemLoadError = collectionAttributesStore.getItemLoadError(props.collectionId);
            if (itemLoadError) {
                return errorMessageAsString(itemLoadError);
            }
            return undefined;
        });
        const collection = computed(() => {
            return collectionStore.getCollectionById(props.collectionId);
        });
        const collectionLoadError = computed(() => {
            if (collection.value) {
                const collectionElementLoadError = collectionStore.getLoadingCollectionElementsError(collection.value);
                if (collectionElementLoadError) {
                    return errorMessageAsString(collectionElementLoadError);
                }
            }
            return undefined;
        });
        watch([attributesLoadError, collectionLoadError], () => {
            if (attributesLoadError.value) {
                errorMessage.value = attributesLoadError.value;
            }
            else if (collectionLoadError.value) {
                errorMessage.value = collectionLoadError.value;
            }
        });
        const databaseKeyFromElements = computed(() => {
            return attributesData.value?.dbkey;
        });
        const datatypeFromElements = computed(() => {
            return attributesData.value?.extension;
        });
        watch(() => collection.value, (newVal) => {
            if (newVal) {
                collectionChangeKey.value++;
                attributesInputs.value = [
                    {
                        name: "name",
                        label: "Name",
                        type: "text",
                        value: newVal.name,
                    },
                ];
            }
        }, { immediate: true });
        function updateInfoMessage(strMessage) {
            infoMessage.value = strMessage;
            successMessage.value = "";
        }
        // TODO: Replace with actual datatype type
        async function clickedSave(attribute, newValue) {
            if (attribute !== "dbkey") {
                // TODO: extend this to other attributes that could be changed
                console.error(`Changing ${attribute} not implemented`);
                return;
            }
            const dbKey = newValue.id;
            const { error } = await GalaxyApi().POST("/api/dataset_collections/{id}/copy", {
                params: { path: { id: props.collectionId } },
                body: { dbkey: dbKey },
            });
            if (error) {
                errorMessage.value = errorMessageAsString(error, `Changing ${attribute} failed.`);
            }
        }
        // TODO: Replace with actual datatype type
        async function clickedConvert(selectedConverter) {
            const url = prependPath(`/api/tools/${selectedConverter.tool_id}/convert`);
            const data = {
                src: "hdca",
                id: props.collectionId,
                source_type: selectedConverter.original_type,
                target_type: selectedConverter.target_type,
            };
            try {
                await axios.post(url, data).catch(handleError);
                successMessage.value = "Conversion started successfully.";
            }
            catch (err) {
                errorMessage.value = errorMessageAsString(err, "Conversion failed.");
            }
        }
        // TODO: Replace with actual datatype type
        async function clickedDatatypeChange(selectedDatatype) {
            if (!currentHistoryId.value) {
                errorMessage.value = "No current history selected.";
                return;
            }
            const { error } = await GalaxyApi().PUT("/api/histories/{history_id}/contents/bulk", {
                params: { path: { history_id: currentHistoryId.value } },
                body: {
                    items: [
                        {
                            history_content_type: "dataset_collection",
                            id: props.collectionId,
                        },
                    ],
                    operation: "change_datatype",
                    params: {
                        type: "change_datatype",
                        datatype: selectedDatatype.id,
                    },
                },
            });
            if (error) {
                errorMessage.value = errorMessageAsString(error, "Datatype change failed.");
                return;
            }
            successMessage.value = "Datatype changed successfully.";
        }
        function handleError(err) {
            errorMessage.value = errorMessageAsString(err, "Datatype conversion failed.");
            if (err?.data?.stderr) {
                jobError.value = err.data;
            }
        }
        function onAttribute(data) {
            for (const key in data) {
                const index = attributesInputs.value?.findIndex((input) => input.name === key);
                if (index !== -1 && attributesInputs.value[index]) {
                    attributesInputs.value[index].value = data[key];
                }
            }
        }
        async function saveAttrs() {
            if (collection.value && attributesInputs.value) {
                const updatedAttrs = attributesInputs.value.reduce((acc, input) => {
                    acc[input.name] = input.value;
                    return acc;
                }, {});
                try {
                    await updateContentFields(collection.value, updatedAttrs);
                    successMessage.value = "Attributes updated successfully.";
                }
                catch (err) {
                    errorMessage.value = errorMessageAsString(err, "Unable to update attributes.");
                }
            }
        }
        return { __sfc: true, props, config, isConfigLoaded, collectionAttributesStore, historyStore, currentHistoryId, collectionStore, jobError, errorMessage, infoMessage, successMessage, attributesInputs, collectionChangeKey, attributesData, attributesLoadError, collection, collectionLoadError, databaseKeyFromElements, datatypeFromElements, updateInfoMessage, clickedSave, clickedConvert, clickedDatatypeChange, handleError, onAttribute, saveAttrs, faBars, faCog, faDatabase, faSave, faTable, FontAwesomeIcon, BAlert, BButton, BSpinner, BTab, BTabs, DatatypesProvider, DbKeyProvider, SuitableConvertersProvider, localize, ChangeDatatypeTab, DatabaseEditTab, SuitableConvertersTab, Heading, FormDisplay, LoadingSpan };
    }
});
